/* eslint-disable react/jsx-no-bind */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSnapshot } from 'valtio';
import * as Pioneer from 'pioneer';

import { refsStore, spacecraftStore } from '../managers/globalState';


const SpacecraftWithInstrumentsEvents = ({ spacecraft, instrumentData, pioneer }) => {
	const [isLoaded, setIsLoaded] = useState(false);

	const { get } = useSnapshot(refsStore.state);
	const { isZoomedOut } = useSnapshot(spacecraftStore.state);

	// Stylized instrument name (ex: 'OCO3' vs 'sc_iss_oco3') - for telling Pioneer which model to outline
	useEffect(() => {
		const sceneCamera = pioneer.get('main', 'camera', 'camera');
		const issModel = pioneer.get('main', 'sc_iss', 'model');

		const allLoaded = () => {
			setIsLoaded(true);

			// When zooming out, no highlight
			if (isZoomedOut) {
				return;
			}

			const instrument = instrumentData.find(instrument => instrument.name === spacecraft);

			// Wait until all instrument resources are loaded
			issModel.setResourcesLoadedCallback(() => {
				// If instrument in focus, highlight
				if (spacecraft !== 'sc_iss' && isLoaded) {
					sceneCamera.setOutline(new Pioneer.Color(0.4, 0.85, 0.93), issModel, instrument.stylizedInstrumentName);
				}
			});
		};

		const onMouseOver = e => {
			const stylizedInstrumentName = e.target.dataset.stylizedInstrumentName ?? e.target.parentNode.dataset.stylizedInstrumentName;
			if (stylizedInstrumentName !== undefined && isLoaded && issModel.getLoadState() === 'loaded') {
				sceneCamera.setOutline(new Pioneer.Color(0.4, 0.85, 0.93), issModel, stylizedInstrumentName);
			}
		};

		const onMouseOut = e => {
			const stylizedInstrumentName = e.target.dataset.stylizedInstrumentName ?? e.target.parentNode.dataset.stylizedInstrumentName;
			if (stylizedInstrumentName !== undefined && isLoaded && issModel.getLoadState() === 'loaded') {
				sceneCamera.setOutline(new Pioneer.Color(0, 0, 0), issModel, stylizedInstrumentName);
			}
		};

		const onClick = async e => {
			const stylizedInstrumentName = e.target.dataset.stylizedInstrumentName ?? e.target.parentNode.dataset.stylizedInstrumentName;
			if (stylizedInstrumentName !== undefined && isLoaded && issModel.getLoadState() === 'loaded') {
				// Which instrument we are focused on
				const instrument = instrumentData.find(instrument => instrument.stylizedInstrumentName === stylizedInstrumentName);
				await get('route').navigate({
					spacecraft: instrument.name,
					vitalsign: 'satellites',
					__remove: ['altid', 'animating', 'start', 'end']
				}, 'spacecraft');
			}
		};

		if (get('scene').allLoaded) {
			allLoaded();
		} else {
			window.addEventListener('allloaded', allLoaded);
		}

		instrumentData.forEach(instrument => {
			const divElement = pioneer.get('main', instrument.name)?.get('div').getDiv();
			divElement.setAttribute('data-stylized-instrument-name', instrument.stylizedInstrumentName);
			divElement.classList.add('clickable');
			divElement.addEventListener('mouseover', onMouseOver);
			divElement.addEventListener('mouseout', onMouseOut);
			divElement.addEventListener('click', onClick);
		});

		return () => {
			window.removeEventListener('allloaded', allLoaded);

			instrumentData.forEach(instrument => {
				const divElement = pioneer.get('main', instrument.name)?.get('div').getDiv();
				divElement.classList.remove('clickable');
				divElement.removeEventListener('mouseover', onMouseOver);
				divElement.removeEventListener('mouseout', onMouseOut);
				divElement.removeEventListener('click', onClick);
			});
		};
	}, [
		get,
		isLoaded,
		isZoomedOut,
		pioneer,
		spacecraft,
		instrumentData
	]);


	return null;
};

/**
 * Wrapper component to use global state
 */
const SpacecraftWithInstruments = props => {
	const { isZoomedOut } = useSnapshot(spacecraftStore.state);
	return !isZoomedOut ? <SpacecraftWithInstrumentsEvents {...props} /> : null;
};

SpacecraftWithInstruments.propTypes = {
	spacecraft: PropTypes.string,
	instrumentData: PropTypes.array,
	pioneer: PropTypes.object // what propType should we classify Pioneer as?
};

export default SpacecraftWithInstruments;
